import { useState } from "react";
import { Sparkles, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { runOnboardingAgent } from "../../lib/onboardingAgent";
import { env } from "../../lib/env";
import demoBundle from "../../data/samples/foyr-demo-bundle.json";
import { cls } from "../../lib/cls";

/* DemoRunButton — one-click end-to-end demo.
 *
 * What it does (real pipeline, no mocking):
 *   1. POSTs a verified-shape Foyr bundle to /api/foyr-bundle/{id}
 *      → Cloudflare KV persists it (7-day TTL).
 *   2. GETs it back to prove the round-trip.
 *   3. Runs the Onboarding Agent on the returned payload — the
 *      same agent that runs against any captured bundle from the
 *      Chrome extension's content script.
 *   4. Persists the typed graph + creates the linked project.
 *   5. Flips active project + reloads the cockpit.
 *
 * Bundle shape mirrors what `neoEditor.projectExporter.export()`
 * returns (verified live 2026-05-17): assetGroupList, prodPartList,
 * productList, neoProductList, sceneData. */

/* Demo bundle + project id come from env + JSON fixture — CLAUDE.md
 * §1 (no hardcoded literals in component code). Swap the env var
 * to demo a different real Foyr ObjectId without touching code. */
const DEMO_PROJECT_ID = env.VITE_FOYR_DEMO_PROJECT_ID;
function buildDemoBundle(): string {
  return JSON.stringify(demoBundle);
}

type Phase =
  | { kind: "idle" }
  | { kind: "running"; step: string }
  | { kind: "done"; nodes: number; edges: number; ms: number }
  | { kind: "error"; message: string };

export function DemoRunButton(): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  async function runDemo(): Promise<void> {
    const t0 = performance.now();
    try {
      const bundle = buildDemoBundle();

      // 1) POST to Cloudflare KV.
      setPhase({ kind: "running", step: "POST bundle → Cloudflare KV" });
      const post = await fetch(`/api/foyr-bundle/${DEMO_PROJECT_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bundle,
      });
      if (!post.ok) throw new Error(`POST returned ${post.status}`);

      // 2) GET it back — proves the round-trip.
      setPhase({ kind: "running", step: "GET bundle ← Cloudflare KV" });
      const get = await fetch(`/api/foyr-bundle/${DEMO_PROJECT_ID}`);
      if (!get.ok) throw new Error(`GET returned ${get.status}`);
      const recv = await get.json() as { ok: boolean; bundle?: string };
      if (!recv.ok || !recv.bundle) throw new Error("KV returned no bundle");

      // 3) Run the Onboarding Agent on the real returned payload.
      setPhase({ kind: "running", step: "Onboarding Agent → typed ontology" });
      const parsed = JSON.parse(recv.bundle) as Record<string, unknown>;
      const projectId = `foyr-${DEMO_PROJECT_ID}`;
      const projectName = (parsed["sceneData"] as { projectName?: string } | undefined)?.projectName ?? "Foyr import";
      const result = runOnboardingAgent(parsed, { id: projectId, name: projectName });

      // 4) Persist linked project + typed graph.
      setPhase({ kind: "running", step: "Persist typed graph + link project" });
      const userRaw = localStorage.getItem(env.VITE_LS_USER_PROJECTS);
      const userArr = (userRaw ? JSON.parse(userRaw) : []) as Array<{ id: string }>;
      const filtered = Array.isArray(userArr) ? userArr.filter((p) => p.id !== projectId) : [];
      filtered.push({
        id: projectId,
        name: projectName,
        city: (parsed["sceneData"] as { city?: string } | undefined)?.city ?? "—",
        client: "Linked from Foyr Neo (demo)",
        client_email: "—",
        bhk: "—",
        budgetINR: 0,
        deadline: "",
        alerts: 0,
        status: "active",
        thumbnailKey: "livingSofa",
        source: `https://neo.foyr.com/toolv2/?project=${DEMO_PROJECT_ID}`,
        foyr_project_id: DEMO_PROJECT_ID,
        created_at: new Date().toISOString(),
      } as unknown as { id: string });
      localStorage.setItem(env.VITE_LS_USER_PROJECTS, JSON.stringify(filtered));

      const graphsRaw = localStorage.getItem(env.VITE_LS_PROJECT_GRAPHS);
      const graphs = (graphsRaw ? JSON.parse(graphsRaw) : {}) as Record<string, unknown>;
      graphs[projectId] = {
        graph: result.graph,
        schema: result.schema,
        trace: result.trace,
        stats: result.stats,
        ingested_at: new Date().toISOString(),
      };
      localStorage.setItem(env.VITE_LS_PROJECT_GRAPHS, JSON.stringify(graphs));

      // 5) Flip active + reload so every cockpit panel re-renders.
      localStorage.setItem(env.VITE_LS_ACTIVE_PROJECT, projectId);
      localStorage.setItem(env.VITE_LS_ACTIVE_PROJECT_LEGACY, projectId);
      window.dispatchEvent(new Event("foyrmind:projectChanged"));

      const ms = Math.round(performance.now() - t0);
      setPhase({ kind: "done", nodes: result.stats.nodes, edges: result.stats.edges, ms });
      // Give the user a moment to read the success state then land on cockpit.
      window.setTimeout(() => { window.location.href = "/"; }, 1500);
    } catch (err) {
      setPhase({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <section className="mt-4 mb-6 rounded-2xl border border-foyr/30 bg-gradient-to-br from-foyr/8 via-cream to-cream-dark/40 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <span className="inline-flex w-9 h-9 rounded-xl bg-ink-950 text-cream items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="eyebrow">One-click demo · for the partnership pitch</div>
          <div className="display-3 text-ink-950 mt-0.5 leading-tight">
            Run the full Foyr → FoyrMind pipeline live.
          </div>
          <p className="text-[12.5px] text-ink-700 mt-1.5 leading-snug max-w-2xl">
            Posts a verified-shape Foyr bundle to <code className="font-mono">/api/foyr-bundle/{DEMO_PROJECT_ID.slice(0, 8)}…</code>{" "}
            (Cloudflare KV), reads it back, runs the Onboarding Agent's auto-schema-detect + ontology mapper,
            persists the typed graph, and lands you on the cockpit with the real graph active.
            Same code path the Chrome extension hits when a designer opens a Foyr project.
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {phase.kind === "idle" ? (
              <button onClick={runDemo} className="btn btn-foyr !text-sm !py-2 !px-3 inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Run the demo
              </button>
            ) : phase.kind === "running" ? (
              <span className="pill bg-foyr/10 text-foyr-deep inline-flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> {phase.step}…
              </span>
            ) : phase.kind === "done" ? (
              <span className="pill bg-fern/15 text-fern inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3 h-3" /> {phase.nodes} nodes · {phase.edges} edges · {phase.ms} ms — loading cockpit
              </span>
            ) : (
              <span className="pill bg-foyr/10 text-foyr-deep inline-flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" /> {phase.message}
              </span>
            )}
            <span className={cls("text-[11px] text-ink-500", phase.kind === "running" && "italic")}>
              {phase.kind === "running"
                ? "Cloudflare KV + Onboarding Agent are running on real infrastructure."
                : phase.kind === "done"
                  ? "Pipeline completed end-to-end."
                  : "5 stages · ≈ 1.5 seconds end-to-end."}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
